import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import type {
  HealthConfig,
  OuraDashboard,
  OuraDayDto,
  OuraIntraday,
  OuraPeriod,
  OuraStatus,
} from '@jordan/types';
import {
  Alert,
  type AppLayoutApi,
  Breadcrumb,
  Button,
  DatePicker,
  DateRangePicker,
  EChart,
  PageHeader,
  SecondarySidebar,
  SecondarySidebarSection,
  SelectMenu,
  Spinner,
} from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { apiGet } from '../../lib/api';
import {
  OURA_METRICS,
  OURA_METRIC_OPTIONS,
  isOuraMetricKey,
  type OuraMetric,
  type OuraMetricKey,
} from './ouraMetrics';

const RHR_COLOR = '#38bdf8'; // light blue — used for the connect prompt
const BAR_IDLE = '#cbd5e1';
const BAR_ACTIVE = '#1e3a8a';

// Intraday periods → bucket size in minutes.
const INTRADAY_BUCKETS: Partial<Record<OuraPeriod, number>> = { hourly: 60, '15min': 15, '5min': 5 };
const isIntradayPeriod = (p: OuraPeriod): boolean => p in INTRADAY_BUCKETS;

// --- Date helpers (local, on YYYY-MM-DD) ---
const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseYmd = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d: Date, n: number) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};
const startOfWeek = (d: Date) => {
  // Monday as the first day of the week.
  const c = new Date(d);
  const dow = (c.getDay() + 6) % 7;
  return addDays(c, -dow);
};

/** "May 4 – 10, 2026" / "Apr 28 – May 4, 2026" / cross-year. */
function formatRange(start: Date, end: Date): string {
  const sM = start.toLocaleString(undefined, { month: 'short' });
  const eM = end.toLocaleString(undefined, { month: 'short' });
  if (start.getFullYear() !== end.getFullYear()) {
    return `${sM} ${start.getDate()}, ${start.getFullYear()} – ${eM} ${end.getDate()}, ${end.getFullYear()}`;
  }
  if (start.getMonth() === end.getMonth()) {
    return `${sM} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${sM} ${start.getDate()} – ${eM} ${end.getDate()}, ${end.getFullYear()}`;
}

/** Single-day label, e.g. "Jun 11, 2026". */
const formatDay = (d: Date) =>
  d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

interface Bucket {
  label: string;
  a: number | null;
  b: number | null;
}

/** How many days the visible window spans for a range period. */
const windowDays = (period: OuraPeriod, rangeDays: number) =>
  period === 'weekly' ? 7 * 12 : period === 'monthly' ? 365 : rangeDays;

/** Average of the non-null numbers, or null when there are none. */
function avg(values: (number | null)[]): number | null {
  const nums = values.filter((v): v is number => v != null);
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

/** Bucket a day series into x-axis points for a range period, reading the two chosen metrics. */
function bucketizeRange(
  days: OuraDayDto[],
  period: OuraPeriod,
  start: Date,
  end: Date,
  mA: OuraMetric,
  mB: OuraMetric,
): Bucket[] {
  const byDate = new Map(days.map((d) => [d.date, d]));

  if (period === 'daily') {
    const out: Bucket[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      const day = byDate.get(ymd(d));
      out.push({ label: String(d.getDate()), a: day?.[mA.field] ?? null, b: day?.[mB.field] ?? null });
    }
    return out;
  }

  // weekly / monthly: group the days that fall in [start, end] into buckets.
  const groups = new Map<string, { label: string; order: number; a: (number | null)[]; b: (number | null)[] }>();
  for (const day of days) {
    const d = parseYmd(day.date);
    if (d < start || d > end) continue;
    let key: string;
    let label: string;
    let order: number;
    if (period === 'weekly') {
      const ws = startOfWeek(d);
      key = ymd(ws);
      label = ws.toLocaleString(undefined, { month: 'short', day: 'numeric' });
      order = ws.getTime();
    } else {
      key = `${d.getFullYear()}-${d.getMonth()}`;
      label = d.toLocaleString(undefined, { month: 'short' });
      order = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    }
    const g = groups.get(key) ?? { label, order, a: [], b: [] };
    g.a.push(day[mA.field]);
    g.b.push(day[mB.field]);
    groups.set(key, g);
  }
  return [...groups.values()]
    .sort((x, y) => x.order - y.order)
    .map((g) => ({ label: g.label, a: avg(g.a), b: avg(g.b) }));
}

/** The per-bucket series for one metric in intraday mode. */
function intradaySeries(it: OuraIntraday, metric: OuraMetric): (number | null)[] {
  const pts = it.points;
  if (metric.intraday === 'hr') return pts.map((p) => p.heartRate);
  if (metric.intraday === 'flat') {
    const v = it.day[metric.field];
    const value = typeof v === 'number' ? v : null;
    return pts.map(() => value);
  }
  // burn-active / burn-total: distribute the day's calorie total by MET weight.
  const total = it.day[metric.field];
  if (typeof total !== 'number') return pts.map(() => null);
  const weight = (met: number | null) =>
    met == null ? 0 : metric.intraday === 'burn-active' ? Math.max(met - 1, 0) : met;
  const sum = pts.reduce((s, p) => s + weight(p.met), 0);
  if (sum <= 0) return pts.map(() => null);
  return pts.map((p) => {
    const w = weight(p.met);
    return w > 0 ? (total * w) / sum : null;
  });
}

function bucketizeIntraday(it: OuraIntraday, mA: OuraMetric, mB: OuraMetric): Bucket[] {
  const a = intradaySeries(it, mA);
  const b = intradaySeries(it, mB);
  return it.points.map((p, i) => ({ label: p.label, a: a[i], b: b[i] }));
}

/** Pearson correlation between two aligned series, over points where both are present. */
function correlation(as: (number | null)[], bs: (number | null)[]): number | null {
  const pairs: { a: number; b: number }[] = [];
  for (let i = 0; i < as.length; i++) {
    const a = as[i];
    const b = bs[i];
    if (a != null && b != null) pairs.push({ a, b });
  }
  const n = pairs.length;
  if (n < 3) return null;
  const ma = pairs.reduce((s, p) => s + p.a, 0) / n;
  const mb = pairs.reduce((s, p) => s + p.b, 0) / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (const p of pairs) {
    num += (p.a - ma) * (p.b - mb);
    dx += (p.a - ma) ** 2;
    dy += (p.b - mb) ** 2;
  }
  const den = Math.sqrt(dx * dy);
  return den === 0 ? null : num / den;
}

// --- Chart option builders ---
function mainOption(buckets: Bucket[], mA: OuraMetric, mB: OuraMetric, intraday: boolean): EChartsOption {
  const metrics = [mA, mB];
  return {
    color: [mA.color, mB.color],
    textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
    grid: { left: 8, right: 8, top: 24, bottom: 8, containLabel: true },
    legend: { show: false },
    tooltip: {
      trigger: 'axis',
      className: 'shadow-sm rounded',
      backgroundColor: 'var(--white)',
      borderColor: 'var(--gray-400)',
      padding: 12,
      textStyle: { color: 'var(--body-color)' },
      formatter: (params: any) => {
        const rows = (params as any[])
          .map((p) => {
            if (p.value == null) return '';
            const m = metrics[p.seriesIndex] ?? mA;
            return `<div>${p.marker} ${m.label}: <b>${m.format(p.value)}</b></div>`;
          })
          .join('');
        return `<div class="fw-semibold mb-1">${params[0]?.axisValue ?? ''}</div>${rows}`;
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: buckets.map((b) => b.label),
      axisLabel: {
        color: 'rgba(var(--body-color-rgb), .65)',
        // Intraday: only label the top of each hour to avoid a crowded axis.
        ...(intraday ? { interval: 0, formatter: (v: string) => (v.endsWith(':00') ? v : '') } : {}),
      },
      axisLine: { lineStyle: { color: 'var(--gray-400)' } },
      axisTick: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: mA.label,
        position: 'left',
        ...(mA.axisInterval ? { interval: mA.axisInterval } : {}),
        axisLabel: { color: 'rgba(var(--body-color-rgb), .65)', formatter: (v: number) => mA.format(v) },
        splitLine: { lineStyle: { color: 'var(--gray-200)', type: 'dashed' } },
      },
      {
        type: 'value',
        name: mB.label,
        position: 'right',
        ...(mB.axisInterval ? { interval: mB.axisInterval } : {}),
        axisLabel: { color: 'rgba(var(--body-color-rgb), .65)', formatter: (v: number) => mB.format(v) },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: mA.label,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: intraday ? 0 : 7,
        yAxisIndex: 0,
        connectNulls: true,
        lineStyle: { width: intraday ? 2 : 3 },
        data: buckets.map((b) => b.a),
      },
      {
        name: mB.label,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: intraday ? 0 : 7,
        yAxisIndex: 1,
        connectNulls: true,
        lineStyle: { width: intraday ? 2 : 3 },
        areaStyle: { opacity: 0.12 },
        data: buckets.map((b) => b.b),
      },
    ],
  } as EChartsOption;
}

function overviewOption(days: OuraDayDto[], start: Date, end: Date, metric: OuraMetric): EChartsOption {
  return {
    color: [metric.color],
    textStyle: { fontFamily: 'var(--body-font-family)' },
    grid: { left: 8, right: 8, top: 22, bottom: 4, containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'var(--white)',
      borderColor: 'var(--gray-400)',
      className: 'shadow-sm rounded',
      textStyle: { color: 'var(--body-color)' },
      formatter: (params: any) => {
        const p = (params as any[])[0];
        if (!p || p.value == null) return `${p?.axisValue ?? ''}: no data`;
        return `${p.axisValue}: <b>${metric.format(p.value)}</b>`;
      },
    },
    xAxis: {
      type: 'category',
      data: days.map((d) => d.date),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: 'var(--gray-300)' } },
      // Month label on the 1st of each month, positioned along the top like the reference.
      position: 'top',
      axisLabel: {
        color: 'rgba(var(--body-color-rgb), .55)',
        interval: 0,
        formatter: (val: string) => {
          const d = parseYmd(val);
          return d.getDate() === 1 ? d.toLocaleString(undefined, { month: 'short' }) : '';
        },
      },
    },
    yAxis: { type: 'value', show: false },
    series: [
      {
        type: 'bar',
        data: days.map((d) => {
          const inRange = d.date >= ymd(start) && d.date <= ymd(end);
          return {
            value: d[metric.field],
            itemStyle: { color: inRange ? BAR_ACTIVE : BAR_IDLE },
          };
        }),
        barWidth: '70%',
      },
    ],
  } as EChartsOption;
}

export function OuraRingPage() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState<HealthConfig | null>(null);
  const [status, setStatus] = useState<OuraStatus | null>(null);
  const [period, setPeriod] = useState<OuraPeriod>('daily');
  const [metricLeft, setMetricLeft] = useState<OuraMetricKey>('sleepScore');
  const [metricRight, setMetricRight] = useState<OuraMetricKey>('restingHr');
  // anchorEnd is the last day of the visible window (the single day in intraday mode).
  const [anchorEnd, setAnchorEnd] = useState<Date>(() => new Date());
  // Explicit window length (days) set by the from/to range picker; null = derive from period.
  const [windowOverride, setWindowOverride] = useState<number | null>(null);
  const [detail, setDetail] = useState<OuraDashboard | null>(null);
  const [intraday, setIntraday] = useState<OuraIntraday | null>(null);
  const [overview, setOverview] = useState<OuraDashboard | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const mA = OURA_METRICS[metricLeft];
  const mB = OURA_METRICS[metricRight];
  const intradayMode = isIntradayPeriod(period);
  const bucketMinutes = INTRADAY_BUCKETS[period] ?? 60;

  const rangeDays = cfg?.ouraDefaultRangeDays ?? 7;
  const overviewDays = cfg?.ouraOverviewDays ?? 180;
  const span = intradayMode ? 1 : (windowOverride ?? windowDays(period, rangeDays));
  const end = anchorEnd;
  const start = useMemo(() => addDays(end, -(span - 1)), [end, span]);

  // Load config + status; seed the period from the configured default.
  useEffect(() => {
    Promise.all([
      apiGet<HealthConfig>('/api/config/health').catch(() => null),
      apiGet<OuraStatus>('/api/health/oura/status'),
    ])
      .then(([c, s]) => {
        if (c) {
          setCfg(c);
          setPeriod(c.ouraDefaultPeriod);
        }
        setStatus(s);
      })
      .catch(() => setErr('Failed to load Oura status.'))
      .finally(() => setLoading(false));
  }, []);

  // Range data (daily / weekly / monthly).
  useEffect(() => {
    if (!status?.connected || intradayMode) return;
    setDetail(null);
    apiGet<OuraDashboard>(`/api/health/oura/dashboard?start=${ymd(start)}&end=${ymd(end)}`)
      .then(setDetail)
      .catch(() => setErr('Could not load Oura data for this range.'));
  }, [status?.connected, intradayMode, start, end, reloadKey]);

  // Intraday data (hourly / 15-min / 5-min) for the anchor day.
  useEffect(() => {
    if (!status?.connected || !intradayMode) return;
    setIntraday(null);
    apiGet<OuraIntraday>(`/api/health/oura/intraday?date=${ymd(end)}&bucket=${bucketMinutes}`)
      .then(setIntraday)
      .catch(() => setErr('Could not load Oura data for this day.'));
  }, [status?.connected, intradayMode, end, bucketMinutes, reloadKey]);

  // Overview strip data (wider, fixed window ending today) — follows the left metric.
  useEffect(() => {
    if (!status?.connected) return;
    const ovEnd = new Date();
    const ovStart = addDays(ovEnd, -(overviewDays - 1));
    apiGet<OuraDashboard>(`/api/health/oura/dashboard?start=${ymd(ovStart)}&end=${ymd(ovEnd)}`)
      .then(setOverview)
      .catch(() => undefined);
  }, [status?.connected, overviewDays]);

  const buckets = useMemo(() => {
    if (intradayMode) return intraday ? bucketizeIntraday(intraday, mA, mB) : [];
    return detail ? bucketizeRange(detail.days, period, start, end, mA, mB) : [];
  }, [intradayMode, intraday, detail, period, start, end, mA, mB]);

  const corr = useMemo(() => correlation(buckets.map((b) => b.a), buckets.map((b) => b.b)), [buckets]);
  const avgA = useMemo(() => avg(buckets.map((b) => b.a)), [buckets]);
  const avgB = useMemo(() => avg(buckets.map((b) => b.b)), [buckets]);

  const connect = useCallback(async () => {
    try {
      const { url } = await apiGet<{ url: string }>('/api/health/oura/connect');
      window.location.href = url;
    } catch {
      setErr('Oura is not configured yet (missing OAuth credentials).');
    }
  }, []);

  const shift = (dir: -1 | 1) => setAnchorEnd((e) => addDays(e, dir * span));
  const reload = () => setReloadKey((k) => k + 1);

  const downloadCsv = () => {
    let rows: (string | number)[][];
    let name: string;
    if (intradayMode) {
      if (!intraday) return;
      rows = [
        ['time', 'heart_rate_bpm', 'met'],
        ...intraday.points.map((p) => [p.label, p.heartRate ?? '', p.met ?? '']),
      ];
      name = `oura-intraday-${ymd(end)}-${bucketMinutes}min.csv`;
    } else {
      if (!detail) return;
      rows = [
        ['date', 'sleep_minutes', 'resting_hr', 'sleep_score', 'readiness_score', 'activity_score', 'steps', 'active_calories', 'total_calories'],
        ...detail.days.map((d) => [
          d.date,
          d.totalSleepMinutes ?? '',
          d.restingHr ?? '',
          d.sleepScore ?? '',
          d.readinessScore ?? '',
          d.activityScore ?? '',
          d.steps ?? '',
          d.activeCalories ?? '',
          d.totalCalories ?? '',
        ]),
      ];
      name = `oura-${ymd(start)}_${ymd(end)}.csv`;
    }
    const csv = rows.map((r) => r.join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const mainOpt = useMemo(() => mainOption(buckets, mA, mB, intradayMode), [buckets, mA, mB, intradayMode]);
  const overviewOpt = useMemo(
    () => (overview ? overviewOption(overview.days, start, end, mA) : null),
    [overview, start, end, mA],
  );

  const periodOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'hourly', label: 'Hourly' },
    { value: '15min', label: '15 minutes' },
    { value: '5min', label: '5 minutes' },
  ];

  const rangeLabel = intradayMode ? formatDay(end) : formatRange(start, end);
  const chartLoading = intradayMode ? !intraday : !detail;
  const hasData = buckets.some((b) => b.a != null || b.b != null);
  const tags = (intradayMode ? intraday?.tags : detail?.tags) ?? [];
  const dataReady = intradayMode ? Boolean(intraday) : Boolean(detail);

  // Page-level secondary sidebar: view controls, a quick summary, navigation and export.
  const secondarySidebar = (api: AppLayoutApi) => {
    const go = (path: string) => {
      navigate(path);
      api.closeSecondarySidebar();
    };
    return (
      <SecondarySidebar
        title="Controls"
        mobileExpanded={api.secondarySidebarExpanded}
        onClose={api.closeSecondarySidebar}
      >
        <SecondarySidebarSection title={intradayMode ? 'View day' : 'View range'}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Button variant="light" className="btn-icon rounded-circle" icon="ph-caret-left" aria-label="Previous" onClick={() => shift(-1)} />
            <span className="fw-semibold text-center small">{rangeLabel}</span>
            <Button variant="light" className="btn-icon rounded-circle" icon="ph-caret-right" aria-label="Next" onClick={() => shift(1)} />
          </div>

          {/* Jump straight to a day (intraday) or an explicit from/to range. */}
          {intradayMode ? (
            <DatePicker
              className="mb-2"
              value={ymd(end)}
              onChange={(v) => v && setAnchorEnd(parseYmd(v))}
            />
          ) : (
            <div className="mb-2">
              <DateRangePicker
                start={ymd(start)}
                end={ymd(end)}
                onChange={({ start: s, end: e }) => {
                  if (!s || !e) return;
                  const from = parseYmd(s);
                  const to = parseYmd(e);
                  setWindowOverride(Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1);
                  setAnchorEnd(to);
                }}
              />
            </div>
          )}

          <SelectMenu
            className="mb-0"
            icon="ph-funnel"
            value={period}
            onChange={(v) => {
              setWindowOverride(null);
              setPeriod(v as OuraPeriod);
            }}
            options={periodOptions}
          />
        </SecondarySidebarSection>

        <SecondarySidebarSection title="Summary">
          <ul className="list-unstyled mb-0">
            <li className="d-flex justify-content-between py-1">
              <span className="text-muted">
                <i className="ph-circle-fill me-2" style={{ color: mA.color, fontSize: 10 }} />
                Avg {mA.label}
              </span>
              <span className="fw-semibold">{avgA == null ? '—' : mA.format(avgA)}</span>
            </li>
            <li className="d-flex justify-content-between py-1">
              <span className="text-muted">
                <i className="ph-circle-fill me-2" style={{ color: mB.color, fontSize: 10 }} />
                Avg {mB.label}
              </span>
              <span className="fw-semibold">{avgB == null ? '—' : mB.format(avgB)}</span>
            </li>
            <li className="d-flex justify-content-between py-1">
              <span className="text-muted">
                <i className="ph-chart-line me-2" />
                Correlation
              </span>
              <span className="fw-semibold">{corr == null ? '—' : `r = ${corr.toFixed(2)}`}</span>
            </li>
          </ul>
        </SecondarySidebarSection>

        <SecondarySidebarSection title="Navigation" flush>
          <ul className="nav nav-sidebar my-2" data-nav-type="accordion">
            <li className="nav-item-header opacity-50">Quick links</li>
            <li className="nav-item">
              <a href="/configuration/health" className="nav-link" onClick={(e) => { e.preventDefault(); go('/configuration/health'); }}>
                <i className="ph-gear me-3" />
                Configure Oura
              </a>
            </li>
            <li className="nav-item">
              <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); reload(); }}>
                <i className="ph-arrows-clockwise me-3" />
                Reload data
              </a>
            </li>
            <li className="nav-item">
              <a href="#" className="nav-link" onClick={(e) => { e.preventDefault(); downloadCsv(); }}>
                <i className="ph-download-simple me-3" />
                Download CSV
              </a>
            </li>
          </ul>
        </SecondarySidebarSection>

        <SecondarySidebarSection title="Data">
          <Button variant="light" icon="ph-download-simple" className="w-100" disabled={!dataReady} onClick={downloadCsv}>
            Download Data
          </Button>
        </SecondarySidebarSection>
      </SecondarySidebar>
    );
  };

  return (
    <AppChrome secondarySidebar={status?.connected ? secondarySidebar : undefined}>
      <PageHeader
        title={
          <>
            Health - <span className="fw-normal">Oura Ring</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Health' }, { label: 'Oura Ring' }]} />}
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {loading ? (
          <Spinner />
        ) : !status?.connected ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-heart" style={{ fontSize: 56, color: RHR_COLOR }} />
              <h5 className="fw-bold mt-3">Connect your Oura Ring</h5>
              <p className="text-muted mb-4">
                Link your Oura account to see your sleep, activity and heart rate here.
              </p>
              <Button variant="primary" icon="ph-link" disabled={!status?.configured} onClick={connect}>
                Connect Oura Ring
              </Button>
              {!status?.configured && (
                <div className="text-muted small mt-3">
                  Oura isn&rsquo;t configured yet — an admin can set it up under Configuration → Health.
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Range heading (controls live in the secondary sidebar) */}
            <div className="d-flex align-items-center mb-3">
              <span className="fw-bold fs-5">
                <i className="ph-calendar-blank me-2 text-muted" />
                {rangeLabel}
              </span>
            </div>

            {/* Overview strip */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body pb-2">
                {overviewOpt ? <EChart option={overviewOpt} height={150} /> : <div style={{ height: 150 }} />}
              </div>
            </div>

            {/* Tags summary */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex align-items-center text-muted mb-2">
                  <i className="ph-tag me-2" />
                  Summary of tags from {rangeLabel}
                </div>
                {tags.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {tags.map((t) => (
                      <span key={t.label} className="badge bg-light text-body border">
                        {t.label} <span className="text-muted">× {t.count}</span>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted small">No tags in this {intradayMode ? 'day' : 'range'}.</span>
                )}
              </div>
            </div>

            {/* Main dual-axis chart */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent d-flex align-items-center gap-2 flex-wrap">
                <div style={{ minWidth: 180 }}>
                  <SelectMenu
                    className="mb-0"
                    value={metricLeft}
                    onChange={(v) => isOuraMetricKey(v) && setMetricLeft(v)}
                    options={OURA_METRIC_OPTIONS}
                  />
                </div>
                <span className="mx-auto text-muted small text-center">
                  {corr == null ? (
                    <>Unknown correlation<br />Not enough data points</>
                  ) : (
                    <>{mA.label} vs {mB.label}<br />r = {corr.toFixed(2)}</>
                  )}
                </span>
                <div style={{ minWidth: 180 }}>
                  <SelectMenu
                    className="mb-0"
                    value={metricRight}
                    onChange={(v) => isOuraMetricKey(v) && setMetricRight(v)}
                    options={OURA_METRIC_OPTIONS}
                  />
                </div>
              </div>
              <div className="card-body">
                {chartLoading ? (
                  <Spinner />
                ) : hasData ? (
                  <EChart option={mainOpt} height={420} />
                ) : (
                  <div className="text-center text-muted py-5">
                    No Oura data for this {intradayMode ? 'day' : 'range'}.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AppChrome>
  );
}
